"use client";
import React from "react";
import styles from "./BottomTrending.module.css";
import Container from "../container";
import { logger } from "@/lib/logger";
import Link from "next/link";
import { isCityAllowed } from "@/lib/cityVisibility";

function BottomTrending({ data, city }) {
  if (!data) return null;

  const { title, trending_events, trending_searches } = data;

  // trending_searches predates per-city scoping and may still be a plain
  // string in older CMS content — normalize both shapes to { term,
  // for_which_city } so unscoped legacy entries keep showing everywhere
  // (isCityAllowed treats a missing/empty for_which_city as unrestricted).
  const validSearches = (
    Array.isArray(trending_searches) ? trending_searches : []
  )
    .map((s) =>
      typeof s === "string"
        ? { term: s, for_which_city: null }
        : s && typeof s === "object"
          ? { term: s.term, for_which_city: s.for_which_city }
          : null,
    )
    .filter((s) => s?.term && s.term.trim())
    .filter((s) => isCityAllowed(s.for_which_city, city));
  const validEvents = (
    Array.isArray(trending_events) ? trending_events : []
  )
    .filter((e) => e && (e.name || e.title || e.slug))
    .filter((e) => isCityAllowed(e.for_which_city, city));

  const hasTrendingSearches = validSearches.length > 0;
  const hasTrendingEvents = validEvents.length > 0;

  if (!hasTrendingSearches && !hasTrendingEvents) return null;

  return (
    <section className={styles.bottomTrendingContainer}>
      <Container>
        <div className={styles.contentWrapper}>
          {/* Section Title */}
          {title && <h2 className={styles.sectionTitle}>{title}</h2>}

          {/* Trending Searches Row — navigates to /search?q=<term> */}
          {hasTrendingSearches && (
            <div className={styles.row}>
              {validSearches.map((search, index) => (
                <React.Fragment key={index}>
                  <Link
                    href={`/search?q=${encodeURIComponent(search.term)}`}
                    className={styles.rowItem}
                    prefetch={false}
                  >
                    {search.term}
                  </Link>
                  {index < validSearches.length - 1 && (
                    <span className={styles.separator}>|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Trending Events Row — navigates to /<slug> */}
          {hasTrendingEvents && (
            <div className={styles.row}>
              {validEvents.map((event, index) => {
                const eventSlug = event.slug ? (event.slug.startsWith("/") ? event.slug : `/${event.slug}`) : "#";
                const eventTitle = event.name || event.title || "Event";
                return (
                  <React.Fragment key={event.event_id ?? event.id ?? index}>
                    <Link
                      href={eventSlug}
                      className={styles.rowItem}
                      prefetch={false}
                    >
                      {eventTitle}
                    </Link>
                    {index < validEvents.length - 1 && (
                      <span className={styles.separator}>|</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
}

export default BottomTrending;
